/**
 * PersonForm - Edit person details
 *
 * Form for creating/updating person information with progressive disclosure
 * of advanced fields. Uses Catalyst form components.
 */

import { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Textarea } from '@/components/ui/catalyst/textarea';
import type {
  CreatePerson,
  ReadPerson,
  UpdatePerson,
} from '@/services/api/partiesService';

interface PersonFormProps {
  person?: ReadPerson;
  matterId: string;
  onSubmit: (data: CreatePerson | UpdatePerson) => void;
  onCancel: () => void;
  onDelete?: (id: string) => void;
}

export const PersonForm = ({
  person,
  matterId,
  onSubmit,
  onCancel,
  onDelete,
}: PersonFormProps) => {
  const [formData, setFormData] = useState<
    Partial<CreatePerson | UpdatePerson>
  >({
    first_name: person?.first_name || '',
    middle_name: person?.middle_name || '',
    last_name: person?.last_name || '',
    suffix: person?.suffix || '',
    date_of_birth: person?.date_of_birth || '',
    stated_age: person?.stated_age || undefined,
    ssn: person?.ssn || '',
    gender: person?.gender || '',
    preferred_language: person?.preferred_language || '',
    drivers_license_number: person?.drivers_license_number || '',
    drivers_license_state_code: person?.drivers_license_state_code || '',
    address_line1: person?.address_line1 || '',
    address_line2: person?.address_line2 || '',
    city: person?.city || '',
    state: person?.state || '',
    zip: person?.zip || '',
    identity_status: person?.identity_status || 'unknown',
    involvement_description: person?.involvement_description || '',
    short_description: person?.short_description || '',
    is_minor: person?.is_minor || false,
    is_claimant: person?.is_claimant || false,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = person
      ? ({ ...formData, id: person.id } as UpdatePerson)
      : ({ ...formData, matter_id: matterId } as CreatePerson);
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Fieldset>
        <FieldGroup>
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>First Name</Label>
              <Input
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => handleChange('first_name', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Last Name</Label>
              <Input
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => handleChange('last_name', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Middle Name</Label>
              <Input
                type="text"
                value={formData.middle_name || ''}
                onChange={(e) => handleChange('middle_name', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Suffix</Label>
              <Input
                type="text"
                value={formData.suffix || ''}
                onChange={(e) => handleChange('suffix', e.target.value)}
                placeholder="Jr., Sr., III"
              />
            </Field>
          </div>

          {/* Key identifiers */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={formData.date_of_birth || ''}
                onChange={(e) => handleChange('date_of_birth', e.target.value)}
              />
            </Field>
            <Field>
              <Label>SSN</Label>
              <Input
                type="text"
                value={formData.ssn || ''}
                onChange={(e) => handleChange('ssn', e.target.value)}
                placeholder="XXX-XX-XXXX"
              />
            </Field>
          </div>

          {/* Involvement description */}
          <Field>
            <Label>Involvement</Label>
            <Textarea
              value={formData.involvement_description || ''}
              onChange={(e) =>
                handleChange('involvement_description', e.target.value)
              }
              placeholder="Brief description of how this person was involved in the incident"
              rows={2}
            />
          </Field>

          {/* Advanced fields - progressive disclosure */}
          {showAdvanced && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Gender</Label>
                  <Input
                    type="text"
                    value={formData.gender || ''}
                    onChange={(e) => handleChange('gender', e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Preferred Language</Label>
                  <Input
                    type="text"
                    value={formData.preferred_language || ''}
                    onChange={(e) =>
                      handleChange('preferred_language', e.target.value)
                    }
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Driver License Number</Label>
                  <Input
                    type="text"
                    value={formData.drivers_license_number || ''}
                    onChange={(e) =>
                      handleChange('drivers_license_number', e.target.value)
                    }
                  />
                </Field>
                <Field>
                  <Label>License State</Label>
                  <Input
                    type="text"
                    value={formData.drivers_license_state_code || ''}
                    onChange={(e) =>
                      handleChange('drivers_license_state_code', e.target.value)
                    }
                    placeholder="CA"
                    maxLength={2}
                  />
                </Field>
              </div>

              <Field>
                <Label>Address Line 1</Label>
                <Input
                  type="text"
                  value={formData.address_line1 || ''}
                  onChange={(e) =>
                    handleChange('address_line1', e.target.value)
                  }
                />
              </Field>

              <Field>
                <Label>Address Line 2</Label>
                <Input
                  type="text"
                  value={formData.address_line2 || ''}
                  onChange={(e) =>
                    handleChange('address_line2', e.target.value)
                  }
                />
              </Field>

              <div className="grid grid-cols-3 gap-4">
                <Field>
                  <Label>City</Label>
                  <Input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>State</Label>
                  <Input
                    type="text"
                    value={formData.state || ''}
                    onChange={(e) => handleChange('state', e.target.value)}
                    maxLength={2}
                  />
                </Field>
                <Field>
                  <Label>ZIP</Label>
                  <Input
                    type="text"
                    value={formData.zip || ''}
                    onChange={(e) => handleChange('zip', e.target.value)}
                  />
                </Field>
              </div>

              <Field>
                <Label>Identity Status</Label>
                <Select
                  value={formData.identity_status || 'unknown'}
                  onChange={(e) =>
                    handleChange('identity_status', e.target.value)
                  }
                >
                  <option value="unknown">Unknown</option>
                  <option value="described">Described</option>
                  <option value="named">Named</option>
                </Select>
              </Field>

              <Field>
                <Label>Short Description</Label>
                <Input
                  type="text"
                  value={formData.short_description || ''}
                  onChange={(e) =>
                    handleChange('short_description', e.target.value)
                  }
                  placeholder="Brief identifier if name unknown"
                />
              </Field>
            </>
          )}

          {/* Toggle advanced fields */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {showAdvanced ? 'Hide' : 'Show'} additional fields
          </button>
        </FieldGroup>
      </Fieldset>

      {/* Actions */}
      <div className="flex items-center justify-between">
        {person && onDelete && (
          <Button type="button" onClick={() => onDelete(person.id)} color="red">
            Delete
          </Button>
        )}
        <div className="flex justify-end gap-3 ml-auto">
          <Button type="button" onClick={onCancel} plain>
            Cancel
          </Button>
          <Button type="submit">{person ? 'Update' : 'Create'} Person</Button>
        </div>
      </div>
    </form>
  );
};
